import { __awaiter } from '../_virtual/_tslib.js';
import MyAlgoConnect from '@randlabs/myalgo-connect';
import { WalletConnectorBase } from '@dynamic-labs/wallet-connector-core';
import { getItemAsync, setItemAsync, removeItemAsync, DynamicError } from '@dynamic-labs/utils';
import { MyAlgoSigner } from './myalgoSigner.js';

const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
const HTTP_STATUS_NOT_FOUND = 404;
class MyAlgoLocalStorageCache {
    constructor() {
        this.SESSION_STORAGE_KEY = 'algorand_myalgo_currentAddress';
        this.LAST_BALANCE_KEY = 'algorand_myalgo_lastBalance';
    }
    getCurrentAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const addr = yield getItemAsync(this.SESSION_STORAGE_KEY);
            if (!addr) {
                return undefined;
            }
            return addr;
        });
    }
    setCurrentAddress(addr) {
        return __awaiter(this, void 0, void 0, function* () {
            setItemAsync(this.SESSION_STORAGE_KEY, addr);
        });
    }
    clearCurrentAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            removeItemAsync(this.SESSION_STORAGE_KEY);
        });
    }
    getLastBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const balance = yield getItemAsync(this.LAST_BALANCE_KEY);
            if (!balance) {
                return undefined;
            }
            return balance;
        });
    }
    setLastBalance(balance) {
        return __awaiter(this, void 0, void 0, function* () {
            setItemAsync(this.LAST_BALANCE_KEY, balance);
        });
    }
}
class MyAlgoWalletConnector extends WalletConnectorBase {
    constructor(opts) {
        super(opts);
        this.name = 'MyAlgo';
        this.canConnectViaCustodialService = true;
        this.connectedChain = 'ALGO';
        this.supportedChains = ['ALGO'];
        this.cache = new MyAlgoLocalStorageCache();
    }
    getClient() {
        if (!this.client) {
            this.client = new MyAlgoConnect();
        }
        return this.client;
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    getDeepLink() {
        return undefined;
    }
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const currentAddress = yield this.cache.getCurrentAddress();
            if (currentAddress) {
                return currentAddress;
            }
            const accounts = yield this.getClient().connect({
                shouldSelectOneAccount: true,
            });
            if (accounts.length > 0) {
                const [{ address }] = accounts;
                yield this.cache.setCurrentAddress(address);
                return address;
            }
            return '';
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = yield this.fetchPublicAddress();
            if (!address)
                throw new DynamicError('MyAlgo Wallet Connector - Not connected!');
            return new MyAlgoSigner(address, this.getClient());
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            const signer = yield this.getSigner();
            const signature = yield signer.signBytes(Buffer.from(messageToSign));
            return Buffer.from(signature).toString('hex');
        });
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = yield this.fetchPublicAddress();
            if (!address)
                throw new DynamicError('getBalance - Not connected!');
            const response = yield fetch(`https://indexer.algoexplorerapi.io/v2/accounts/${address}`);
            if (!response.ok) {
                // if the request fails due to rate limits, return cached value
                if (response.status === HTTP_STATUS_TOO_MANY_REQUESTS) {
                    return this.cache.getLastBalance();
                }
                // new accounts not yet indexed will return a 404
                if (response.status === HTTP_STATUS_NOT_FOUND) {
                    return '0';
                }
                return undefined;
            }
            const accountInfo = yield response.json();
            if (!accountInfo.account) {
                return undefined;
            }
            const balance = accountInfo.account.amount.toString();
            yield this.cache.setLastBalance(balance.toString());
            return balance.toString();
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.cache.clearCurrentAddress();
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const currentAddress = yield this.cache.getCurrentAddress();
            return currentAddress ? [currentAddress] : [];
        });
    }
}

export { MyAlgoWalletConnector };
